import * as React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';

import {
    Autocomplete,
    Button,
    Chip,
    debounce,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography
} from '@mui/material';

import useAuth from 'hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GenericSkeleton from 'ui-component/skeleton/GenericSkeleton';
import SubCard from 'ui-component/cards/SubCard';
import { gridSpacing } from 'store/constant';
import Tag from 'types/tag';
import TagService from 'services/TagService';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import useConfig from 'hooks/useConfig';
import Utilities from 'utils/utilities';
import Asset from 'types/asset';
import AssetService from 'services/AssetService';
import SelectOption from 'types/selectOption';
import frequencyConfList from 'types/frequencyConf';

const AssetDetails = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [tags, setTags] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [tagToSearch, setTagToSearch] = useState<string>('');
    const [asset, setAsset] = useState<Asset | undefined>(undefined);
    const [assetCopy, setAssetCopy] = useState<Asset | undefined>(undefined);
    const [acceleration, setAcceleration] = useState<string>('');
    const theme = useTheme();
    const { locale } = useConfig();
    const utilities = new Utilities();
    const assetService = new AssetService();
    const tagService = new TagService();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dialogDelete, setDialogDelete] = useState(false);
    const [dialogAssetNotExist, setDialogAssetNotExist] = useState(false);
    const frequencyConf: SelectOption[] = frequencyConfList(t);
    useEffect(() => {
        (async () => {
            if (searchParams.get('id')!) {
                await fetchSingleElement();
            } else {
                setDialogAssetNotExist(true);
            }
        })();
    }, []);

    useEffect(() => {
        const e = {
            ...assetCopy!,
            tags: selectedTags
        };
        setAssetCopy(e);
    }, [selectedTags]);

    useEffect(() => {
        (async () => {
            if (assetCopy !== undefined) {
                await fetchTags();
            }
        })();
    }, [assetCopy, tagToSearch, asset]);

    const fetchSingleElement = async () => {
        setIsLoading(true);
        const assetRetrieved = await assetService.getAsset(searchParams.get('id')!);
        if (assetRetrieved) {
            if (assetRetrieved?.acceleration !== undefined) {
                setAcceleration(assetRetrieved.acceleration.toFixed(2));
            }
            setAsset(assetRetrieved!);
            setAssetCopy(assetRetrieved!);
            setSelectedTags(assetRetrieved?.tags || []);
            await fetchTags();
        } else {
            setDialogAssetNotExist(true);
        }
        setIsLoading(false);
    };

    const fetchTags = useCallback(
        debounce(async () => {
            if (asset && asset.id) {
                const tagResult = await tagService.getAssetTags(tagToSearch, assetCopy!.id, user?.tenantId!);
                setTags(tagResult);
            }
        }, 700),
        [tagToSearch, user?.tenantId, asset]
    );

    const handleTagChange = (_: React.SyntheticEvent, newValue: Tag[]) => {
        const uniqueTags = newValue
            .map((tag) => ({ ...tag, name: tag.name.trim() }))
            .filter((tag, index, self) => index === self.findIndex((t) => t.name.toLowerCase() === tag.name.toLowerCase()));
        setSelectedTags(uniqueTags);

        const removedTags = selectedTags.filter((tag) => !uniqueTags.some((selectedTag) => selectedTag.id === tag.id));

        setTags((prev) => {
            const uniqueTags = [...prev, ...removedTags].filter(
                (tag, index, self) => index === self.findIndex((t) => t.name.trim().toLowerCase() === tag.name.trim().toLowerCase())
            );
            return uniqueTags;
        });
    };

    const handleTagInputChange = (event: React.KeyboardEvent | React.FocusEvent, value: string) => {
        const trimmedValue = value.trim().toLowerCase();
        const containsComma = trimmedValue.includes(',');
        if (containsComma) {
            dispatch(startSnackbar(t('ERROR_ADDING_TAG_REMOVE_COMMA'), 'error'));
            return;
        }

        setTagToSearch(value);

        // Verifica se l'input è valido
        if (trimmedValue && !containsComma && (event.type === 'blur' || (event as React.KeyboardEvent).key === 'Enter')) {
            const existingTag = tags.find((tag) => tag.name.trim().toLowerCase() === trimmedValue);
            const alreadySelected = selectedTags.some((tag) => tag.name.trim().toLowerCase() === trimmedValue);

            if (!alreadySelected) {
                if (existingTag) {
                    setSelectedTags((prev) => [...prev, existingTag]);
                } else {
                    const newTag: Tag = { id: uuidv4(), name: trimmedValue };
                    setTags((prev) => [...prev, newTag]);
                    setSelectedTags((prev) => [...prev, newTag]);
                }
            }
        }
    };

    const onSave = async (asset: Asset) => {
        if (asset && utilities.isNotNullOrEmpty(asset.name)) {
            setIsLoading(true);
            const response = await assetService.editAsset(asset);
            if (response) {
                await fetchSingleElement();
                dispatch(startSnackbar(t('DEVICE_EDITED_SUCCESSFULLY'), 'success'));
            } else {
                setIsLoading(false);
            }
        } else {
            dispatch(startSnackbar(t('NAME_IS_MANDATORY'), 'error'));
        }
    };

    const onDelete = async (asset: Asset) => {
        setIsLoading(true);
        const result = await assetService.deleteAsset(asset.id);
        if (result) {
            dispatch(startSnackbar(t('ASSET_DELETED_SUCCESSFULLY'), 'success'));
            navigate('/assets');
        } else {
            setIsLoading(false);
            dispatch(startSnackbar(t('ERROR_DURING_DELETE_ASSET'), 'error'));
        }
    };

    const handleFrequency = (e: any) => {
        const { value } = utilities.getFormValue(e);
        const newAsset = {
            ...assetCopy!,
            frequency: parseFloat(value)
        };
        setAssetCopy(newAsset);
    };

    return !isLoading ? (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
                <SubCard>
                    <Grid container spacing={gridSpacing} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={assetCopy?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...assetCopy!,
                                            name: d.target.value
                                        };
                                        setAssetCopy(e);
                                    }}
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                id="size-small-filled-multi"
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '54px',
                                        padding: 0
                                    },
                                    '& .MuiFilledInput-root': { backgroundColor: '#fff' },
                                    '& .MuiFilledInput-root:hover': { backgroundColor: '#fff' },
                                    '& #size-small-filled-multi': { backgroundColor: 'secondary' }
                                }}
                                options={tags.filter(
                                    (tag) => !selectedTags.some((selected) => selected.name.toLowerCase() === tag.name.toLowerCase())
                                )}
                                getOptionLabel={(option) => option.name}
                                value={selectedTags}
                                onChange={handleTagChange}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return <Chip color="secondary" key={key} label={option.name} size="small" {...tagProps} />;
                                    })
                                }
                                onInputChange={(event: any, value, reason) => {
                                    if (reason === 'input') handleTagInputChange(event, value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Tags"
                                        onKeyDown={(event: any) => handleTagInputChange(event, event.target.value)}
                                        onBlur={(event) => handleTagInputChange(event, event.target.value)}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} sx={{ mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('FREQUENCY')}</InputLabel>
                                <Select
                                    disabled={!!assetCopy?.halogPairDate}
                                    label={t('FREQUENCY')}
                                    name="frequency"
                                    onChange={handleFrequency}
                                    sx={{
                                        color: 'white',
                                        height: '56px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    value={Math.floor(assetCopy?.frequency!)}
                                >
                                    {frequencyConf.map((frequency) => (
                                        <MenuItem value={frequency.value} key={frequency.value}>
                                            {t(frequency.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sx={{ mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('ACCELERATION')}</InputLabel>
                                <OutlinedInput
                                    disabled={!!assetCopy?.halogPairDate}
                                    value={acceleration}
                                    onBlur={(e) => {
                                        let value = parseFloat(e.target.value || '0').toFixed(2);
                                        if (isNaN(Number(value))) {
                                            value = '0';
                                        }
                                        setAcceleration(value);
                                        const updatedAsset = {
                                            ...assetCopy!,
                                            acceleration: parseFloat(value)
                                        };
                                        setAssetCopy(updatedAsset);
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setAcceleration(value);
                                    }}
                                    type="number"
                                    label={t('ACCELERATION')}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </SubCard>
            </Grid>
            {assetCopy?.halogPairDate && (
                <Grid item xs={12}>
                    <SubCard title={t('PAIRED_INFO')}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel> {t('DEVICE_NAME')}</InputLabel>
                                    <OutlinedInput disabled defaultValue={assetCopy?.halogName} label={t('DEVICE_NAME')} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel> {t('PAIR_DATE')}</InputLabel>
                                    <OutlinedInput
                                        disabled
                                        defaultValue={utilities.formatDate(assetCopy?.halogPairDate, locale)}
                                        label={t('PAIR_DATE')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            )}
            <Grid
                container
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    paddingLeft: '25px'
                }}
            >
                <Grid item>
                    <Button
                        style={{
                            backgroundColor: theme.palette.error.main,
                            borderRadius: 7,
                            border: '2px solid',
                            borderColor: theme.palette.error.main
                        }}
                        onClick={() => setDialogDelete(true)}
                    >
                        <Typography sx={{ color: 'white' }}> {t('DELETE')}</Typography>
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        onClick={() => navigate('/halogs')}
                        autoFocus
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.secondary.main,
                            borderRadius: 7,
                            marginRight: 10
                        }}
                    >
                        <Typography sx={{ color: theme.palette.primary.main, textTransform: 'none' }}> {t('RETURN_TO_DEVICES')}</Typography>
                    </Button>
                    <Button
                        onClick={() => onSave(assetCopy!)}
                        variant="text"
                        style={{
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            borderRadius: 7,
                            background: theme.palette.primary.main
                        }}
                    >
                        <Typography sx={{ color: '#fff' }}> {t('SAVE')}</Typography>
                    </Button>
                </Grid>
            </Grid>
            <ConfirmatioDialog
                isVisible={dialogDelete}
                onClose={() => {
                    setDialogDelete(false);
                }}
                onConfirm={() => {
                    setDialogDelete(false);
                    onDelete(asset!);
                }}
            />
            <ConfirmatioDialog
                isVisible={dialogAssetNotExist}
                titleText="ASSET_NOT_EXIST"
                bodyText="ASSET_NOT_EXIST_BODY"
                primaryButtonText="Ok"
                onConfirm={() => {
                    setDialogAssetNotExist(false);
                    navigate('/assets');
                }}
            />
        </Grid>
    ) : (
        <GenericSkeleton />
    );
};

export default AssetDetails;
